import type {KnowledgeMap, KnowledgeNode, PDFStructure} from '@reados/contracts';
import {parsePageLocations} from './state';

export type KnowledgeNodeEditMode = 'edit' | 'sibling' | 'child';
export type KnowledgeMapViewport = {x: number; y: number; scale: number};

export function knowledgeMapViewport(value: unknown): KnowledgeMapViewport | null {
  if (!value || typeof value !== 'object') return null;
  const {x, y, scale} = value as KnowledgeMapViewport;
  return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(scale) && scale > 0
    ? {x, y, scale} : null;
}

/** Resolve only against the current document, retaining book labels and ranges. */
export function knowledgeMapPages(pages: string, structure: Pick<PDFStructure, 'labels' | 'pageCount'>): number[] {
  const locations = pages.split(/[,，]/).map(value => value.trim()).filter(value => {
    if (!value) return false;
    const exact = structure.labels.filter(item => item.label === value);
    if (exact.length) return exact.length === 1;
    // A repeated book label cannot identify a physical destination by itself.
    const endpoints = /^(.+?)\s*[-–]\s*(.+)$/.exec(value)?.slice(1) ?? [value];
    return endpoints.every(label => structure.labels.filter(item => item.label === label.trim()).length <= 1);
  });
  return parsePageLocations(locations, structure.labels).filter(page => Number.isInteger(page) && page >= 1 && page <= structure.pageCount);
}

export function knowledgeMapPageNumbers(map: KnowledgeMap, structure: Pick<PDFStructure, 'labels' | 'pageCount'>): Record<string, number> {
  return Object.fromEntries(map.nodes.flatMap(node => {
    const page = knowledgeMapPages(node.pages, structure)[0];
    return page ? [[node.id, page]] : [];
  }));
}

export function knowledgeNodeSubtree(map: KnowledgeMap, nodeID: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const node of map.nodes) if (node.parentId) children.set(node.parentId, [...(children.get(node.parentId) ?? []), node.id]);
  const result = new Set<string>();
  const queue = [nodeID];
  for (let index = 0; index < queue.length; index++) {
    const id = queue[index];
    if (result.has(id)) continue;
    result.add(id);
    queue.push(...(children.get(id) ?? []));
  }
  return result;
}

export function editKnowledgeNode(map: KnowledgeMap, nodeID: string, mode: KnowledgeNodeEditMode, fields: Pick<KnowledgeNode, 'title' | 'pages'>, newID?: string): KnowledgeMap {
  const reference = map.nodes.find(node => node.id === nodeID);
  if (!reference) throw new Error('这个知识节点已不存在，请重新打开知识地图。');
  const title = fields.title.trim(), pages = fields.pages.trim();
  if (!title) throw new Error('请输入节点名称。');
  if (mode === 'edit') return {...map, nodes: map.nodes.map(node => node.id === nodeID ? {...node, title, pages} : node)};
  if (!newID || map.nodes.some(node => node.id === newID)) throw new Error('无法创建知识节点，请重试。');
  const node: KnowledgeNode = {id: newID, title, pages, parentId: mode === 'child' ? nodeID : reference.parentId ?? null};
  const subtree = knowledgeNodeSubtree(map, nodeID);
  const insertAfter = Math.max(...map.nodes.flatMap((item, index) => subtree.has(item.id) ? [index] : []));
  return {...map, nodes: [...map.nodes.slice(0, insertAfter + 1), node, ...map.nodes.slice(insertAfter + 1)]};
}

export function deleteKnowledgeNode(map: KnowledgeMap, nodeID: string): KnowledgeMap {
  const deleted = knowledgeNodeSubtree(map, nodeID);
  return {...map, nodes: map.nodes.filter(node => !deleted.has(node.id))};
}
